import AppContext from 'App/AppContext';
import { IPLabel, IPSpinner, IPText } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { FluentUIPieChart, IPieChartDataPoint } from 'components/Charts/PieChart';
import HrefLink from 'components/Links/HrefLink';
import EntityPicker from 'components/Pickers/EntityPicker';
import ListRiskScore from 'components/Risk/ListRiskScore';
import { globalStackTokensGapSmall } from 'globalStyles';
import Control from 'models/control';
import Entity, { EntityTypes } from 'models/entity';
import { EntityStatus } from 'models/entityStatus';
import Risk from 'models/risk';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetControlsForRisk } from 'services/Api/controlService';
import { apiGetRisk, apiGetRisks } from 'services/Api/riskService';
import { apiRequest } from 'services/Auth/authConfig';
import { overflow } from 'utils/string';
import { IWidgetConfigRendererProps } from '../WidgetConfigRenderer';
import { IWidgetRendererProps } from '../WidgetRenderer';

export class WidgetSingleRiskConfig {
  riskId: number;

  constructor() {
    this.riskId = 0;
  }

  load(raw: string | undefined) {
    if (raw) {
      try {
        const newRawConfig = JSON.parse(raw);
        this.riskId = newRawConfig.riskId ?? 0;
      } catch {
        //ignore
      }
    }
  }

  clone(): WidgetSingleRiskConfig {
    const newConfig = new WidgetSingleRiskConfig();
    newConfig.riskId = this.riskId;

    return newConfig;
  }
}

interface IWidgetSingleRiskProps extends IWidgetRendererProps {}

const WidgetSingleRisk: FunctionComponent<IWidgetSingleRiskProps> = (props: IWidgetSingleRiskProps) => {
  const { t } = useTranslation(['control', 'controlRisks']);
  const appContext = useContext(AppContext);
  const [risk, setRisk] = useState<Risk | undefined>(undefined);
  const [controls, setControls] = useState<Control[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<IPieChartDataPoint[]>([]);

  useEffect(() => {
    const loadConfig = (): WidgetSingleRiskConfig => {
      const newConfig = new WidgetSingleRiskConfig();
      newConfig.load(props.widget.widgetConfig);

      return newConfig;
    };

    const loadData = async () => {
      try {
        if (isLoading) return;
        setIsLoading(true);

        const config = loadConfig();

        if (config && config.riskId) {
          const accessToken = await appContext.getAccessToken(apiRequest.scopes);
          const [risks, riskControls] = await Promise.all([
            apiGetRisk(config.riskId, accessToken, appContext),
            apiGetControlsForRisk(config.riskId, accessToken, appContext.globalDataCache),
          ]);

          setRisk(risks.risks.length > 0 ? risks.risks[0] : undefined);
          setControls(riskControls);
          setData(getRiskChartData(riskControls));
        }
      } catch (err) {
        appContext.setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  const getRiskChartData = (riskControls: Control[]): IPieChartDataPoint[] => {
    const data: IPieChartDataPoint[] = [];

    if (!riskControls) return data;

    const item1: IPieChartDataPoint = {
      name: Control.getControlStateText(EntityStatus.Todo, t),
      value: riskControls.filter((c) => c.state === EntityStatus.Todo).length,
      color: '#ad2b41',
    };
    const item2: IPieChartDataPoint = {
      name: Control.getControlStateText(EntityStatus.Designing, t),
      value: riskControls.filter((c) => c.state === EntityStatus.Designing).length,
      color: '#5a942b',
    };
    const item3: IPieChartDataPoint = {
      name: Control.getControlStateText(EntityStatus.Implementing, t),
      value: riskControls.filter((c) => c.state === EntityStatus.Implementing).length,
      color: '#dade62',
    };
    const item4: IPieChartDataPoint = {
      name: Control.getControlStateText(EntityStatus.Implemented, t),
      value: riskControls.filter((c) => c.state === EntityStatus.Implemented).length,
      color: '#62a4de',
    };

    if (item1.value > 0) data.push(item1);
    if (item2.value > 0) data.push(item2);
    if (item3.value > 0) data.push(item3);
    if (item4.value > 0) data.push(item4);

    return data;
  };

  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  if (!risk) {
    //config is invalid
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPText>{t('widgets:SingleRisk.Config.Invalid')}</IPText>
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill horizontalAlign="center" tokens={globalStackTokensGapSmall}>
      <IPStack.Item>
        <IPText>
          <HrefLink href={`/risk/${risk.riskId}`}>{overflow(risk.name, 80)}</HrefLink>
        </IPText>
      </IPStack.Item>
      <IPStack.Item grow>
        <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
          {data.length > 0 && (
            <FluentUIPieChart width={280} height={150} label={controls.length.toString()} data={data} />
          )}
          {data.length === 0 && <IPText>{t('widgets:SingleRisk.NoControls')}</IPText>}
        </IPStack>
      </IPStack.Item>
      <IPStack.Item>
        <IPStack horizontal horizontalAlign="center" tokens={globalStackTokensGapSmall}>
          <IPText>{t('widgets:SingleRisk.Status')}</IPText>
          {risk.getCurrentScore() > 0 && <ListRiskScore risk={risk} timeline="current" />}
          <IPText>{appContext.globalDataCache.riskStates.get(risk.riskStateId).state}</IPText>
        </IPStack>
      </IPStack.Item>
    </IPStack>
  );
};

export default WidgetSingleRisk;

//
// Config
//

interface IWidgetConfigSingleRiskProps extends IWidgetConfigRendererProps {}

export const WidgetConfigSingleRisk: FunctionComponent<IWidgetConfigSingleRiskProps> = (
  props: IWidgetConfigSingleRiskProps,
) => {
  const { t } = useTranslation(['widgets', 'translation', 'dashboard']);
  const appContext = useContext(AppContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [riskEntities, setRiskEntities] = useState<Entity[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<Entity | undefined>(undefined);
  const [currentWidgetId, setCurrentWidgetId] = useState<number>(-1);

  useEffect(() => {
    if (currentWidgetId === -1 || currentWidgetId !== props.widget.widgetId) {
      setCurrentWidgetId(props.widget.widgetId);
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  const loadConfig = (): WidgetSingleRiskConfig => {
    let newRawConfig = new WidgetSingleRiskConfig();
    if (props.widget.widgetConfig) {
      newRawConfig = JSON.parse(props.widget.widgetConfig);
    }

    return newRawConfig;
  };

  const loadData = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      const config = loadConfig();

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const riskCol = await apiGetRisks(accessToken, appContext);

      const entities = getEntitiesFromRisks(riskCol.risks);
      setRiskEntities(entities);

      if (config.riskId) {
        const entity = entities.find((e) => e.entityId === config.riskId);
        setSelectedEntity(entity);
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getEntitiesFromRisks = (risks: Risk[]): Entity[] => {
    const entities: Entity[] = [];

    entities.push(
      ...risks.map((c) => {
        const newEntity = new Entity();
        newEntity.entityId = c.riskId;
        newEntity.typeOfEntity = EntityTypes.Risk;
        newEntity.entityName = c.name;
        newEntity.entityCode = c.code;

        return newEntity;
      }),
    );

    return entities;
  };

  const getConfig = (entity: Entity): WidgetSingleRiskConfig => {
    const config = new WidgetSingleRiskConfig();
    config.riskId = entity.entityId;

    return config;
  };

  const onAddSelectedEntity = (entity: Entity) => {
    setSelectedEntity(entity);
    const config = getConfig(entity);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const onRemoveSelectedEntity = () => {
    setSelectedEntity(undefined);
    const config = new WidgetSingleRiskConfig();
    props.onUpdateConfig(JSON.stringify(config), false);
  };

  return (
    <IPStack verticalFill>
      <IPStack.Item>
        <IPLabel>{t('widgets:SingleRisk.Config.Risk.Label')}</IPLabel>
        <EntityPicker
          entities={riskEntities}
          isLoading={isLoading}
          addSelectedEntity={onAddSelectedEntity}
          clearSearchText={onRemoveSelectedEntity}
          loadData={loadData}
          setSearchTextToSelectedEntity={true}
          selectedEntity={selectedEntity}
          isOnPanel={true}
        ></EntityPicker>
      </IPStack.Item>
    </IPStack>
  );
};
